import axios from 'axios';

const DIRECTION_API_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes';
const WEATHER_API_URL = process.env.REACT_APP_WEATHER_API_URI + '/api/hourly-forecast';
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const getHeaders = () => ({
  'x-goog-api-key': API_KEY,
  'x-goog-fieldmask': '*',
});

const buildRequestBody = (body) => ({
  ...body,
  polylineQuality: 'high_quality',
  computeAlternativeRoutes: true,
});

const getDirections = async (body) => {
  try {
    const response = await axios.post(DIRECTION_API_URL, buildRequestBody(body), {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching directions:', error);
    throw error;
  }
};

const calculateRouteETAs = (route) => {
  try {
    let currentETA = new Date();
    const etas = [currentETA.toISOString()]; // Start with origin ETA

    // Calculate ETAs for each leg
    route.legs.forEach((leg) => {
      const durationInSeconds = parseInt(leg.duration.replace('s', ''));
      currentETA = new Date(currentETA.getTime() + durationInSeconds * 1000);

      if (isNaN(currentETA.getTime())) {
        throw new Error('Invalid ETA calculation');
      }

      etas.push(currentETA.toISOString());
    });

    // Add ETAs to route
    route.etas = etas;
    return route;
  } catch (error) {
    console.error('Error calculating route ETAs:', error);
    // Return the original route without ETAs if calculation fails
    return route;
  }
};

const matchWeatherWithETAs = (stops, route, timeZone) => {
  const weatherData = [];
  let originTime;
  stops.forEach((stop, stopIndex) => {
    const eta = !!route?.etas ? new Date(route.etas[stopIndex]) : new Date();
    const etaInTimeZone = new Date(eta.toLocaleString('en-US', { timeZone }));
    // Find the weather interval that contains the ETA
    const matchingWeather = stop?.weatherData?.find((weather) => {
      const startTime = new Date(weather.interval.startTime);
      const endTime = new Date(weather.interval.endTime);
      return eta >= startTime && eta < endTime;
    });
    let dayDiff = 0;
    if (stopIndex === 0) {
      originTime = etaInTimeZone;
    } else {
      dayDiff = etaInTimeZone.getDate() - originTime.getDate();
    }

    if (matchingWeather) {
      weatherData.push({
        stopIndex,
        eta: etaInTimeZone.toISOString(),
        dayDiff,
        interval: matchingWeather.interval,
        degree: matchingWeather.degree,
        iconUrl: matchingWeather.iconUrl,
      });
    }
  });

  return weatherData;
};

const formatTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getWeatherData = async (locationData) => {
  try {
    const { lat, lng } = locationData.position;
    const response = await axios.get(WEATHER_API_URL, {
      params: {
        latitude: lat,
        longitude: lng,
        hours: 24,
      },
    });

    // Find the most relevant forecast hour
    const forecastHours = response.data.forecastHours;
    const weatherData = forecastHours.map((forecastHour) => ({
      interval: forecastHour.interval,
      degree: forecastHour.temperature.degrees,
      iconUrl: forecastHour.weatherCondition.iconBaseUri + '.png',
    }));

    return {
      weatherData,
      timeZone: response.data.timeZone.id,
    };
  } catch (error) {
    console.error('Error fetching weather data:', error);
    throw error;
  }
};

export { getDirections, getWeatherData, calculateRouteETAs, matchWeatherWithETAs, formatTime };
